import {
  GlobalStyles,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import type { OutlinedInputProps } from '@mui/material';
import { useRef, useState } from 'react';
import { VisibleOff, VisibleOn } from '@tbd/life-jsx/SystemIcons';

interface State {
  password: string;
  showPassword: boolean;
}

const initialState = {
  password: '',
  showPassword: false,
};

interface PasswordInputProps extends OutlinedInputProps {
  // require autoComplete
  autoComplete: OutlinedInputProps['autoComplete'];
  onPasswordChange?: (password: string) => void;
}

export function PasswordInput({
  onPasswordChange,
  onChange,
  ...props
}: PasswordInputProps) {
  const [values, setValues] = useState<State>(initialState);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValues((prev) => ({ ...prev, [prop]: newValue }));

      if (prop === 'password') {
        onPasswordChange?.(newValue);
        onChange?.(event);
      }
    };

  const handleClickShowPassword = () => {
    // Store current cursor position before changing type
    const input = inputRef.current;
    const cursorPosition = input?.selectionStart ?? 0;

    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));

    // Use requestAnimationFrame to restore cursor position after DOM update
    requestAnimationFrame(() => {
      if (input) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    // Prevent losing focus when clicking the visibility toggle
    event.preventDefault();
  };

  return (
    <>
      <GlobalStyles
        styles={{
          // Hide the password reveal button in Edge
          // https://learn.microsoft.com/en-us/microsoft-edge/web-platform/password-reveal
          '::-ms-reveal': {
            display: 'none',
          },
        }}
      />
      <OutlinedInput
        // these are the defaults
        fullWidth
        id="password"
        name="password"
        required={true}
        onChange={handleChange('password')}
        type={values.showPassword ? 'text' : 'password'}
        value={values.password}
        inputRef={inputRef}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="Toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {values.showPassword ? <VisibleOff /> : <VisibleOn />}
            </IconButton>
          </InputAdornment>
        }
        //   spread the props if users want to override them
        {...props}
      />
    </>
  );
}
